import { Trans } from "@lingui/react/macro";
import {
  Box,
  Button,
  Container,
  type ContainerProps,
  Dialog,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useLocation, useParams } from "react-router";
import { useAppConfig } from "@/contexts/AppConfigContext";
import type { CookieBannerDisplay } from "@/contexts/FeaturesContext";

interface CookieBannerProps {
  display: CookieBannerDisplay;
  inlineMaxWidth?: ContainerProps["maxWidth"];
  onOnlyNecessary: () => void;
  onAcceptAll: () => void;
}

export default function CookieBanner({
  display,
  inlineMaxWidth,
  onOnlyNecessary,
  onAcceptAll,
}: CookieBannerProps) {
  const params = useParams<{ language?: string; campground?: string }>();
  const location = useLocation();
  const { privacyPolicyUrl } = useAppConfig();
  const isModal = display === "modal";

  const cookiePolicyLink = privacyPolicyUrl ? (
    <MuiLink
      href={privacyPolicyUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: "inherit", textDecorationColor: "inherit" }}
    >
      <Trans>Privacy Policy</Trans>
    </MuiLink>
  ) : (
    <MuiLink
      component={Link}
      to={`/${params.language}/${params.campground}/privacy-policy`}
      state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
      sx={{ color: "inherit", textDecorationColor: "inherit" }}
    >
      <Trans>Privacy Policy</Trans>
    </MuiLink>
  );

  const content = (
    <>
      <Typography id="cookie-banner-title" variant="h4" sx={{ mb: 1.5 }}>
        <Trans>Cookies &amp; Privacy</Trans>
      </Typography>
      <Typography id="cookie-banner-description" variant="body2" sx={{ lineHeight: 1.45, mb: 2 }}>
        <Trans>
          We use necessary cookies to make this booking widget work. With your permission, we’d also
          like to use optional analytics cookies to help us understand and improve your booking
          experience. Learn more in our {cookiePolicyLink}.
        </Trans>
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <Button
          variant={isModal ? "outlined" : "contained"}
          onClick={onOnlyNecessary}
          sx={isModal ? modalSecondaryButtonSx : actionButtonSx}
        >
          <Trans>Only necessary</Trans>
        </Button>
        <Button
          variant="contained"
          onClick={onAcceptAll}
          sx={isModal ? modalPrimaryButtonSx : actionButtonSx}
        >
          <Trans>Accept all</Trans>
        </Button>
      </Stack>
    </>
  );

  if (display === "hidden") return null;

  if (display === "modal") {
    return (
      <Dialog
        open
        maxWidth={false}
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        onClose={() => undefined}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(3px)",
            },
          },
          paper: {
            sx: {
              ...surfaceSx,
              width: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
              m: { xs: 2, sm: 3 },
              borderColor: "divider",
              backgroundColor: "background.paper",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              color: "text.primary",
            },
          },
        }}
      >
        {content}
      </Dialog>
    );
  }

  if (display === "inline") {
    return (
      <Box sx={{ width: "100%", mt: 2 }}>
        <Container maxWidth={inlineMaxWidth} sx={{ px: 2 }}>
          <Box
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-banner-title"
            aria-describedby="cookie-banner-description"
            data-cookie-banner-display={display}
            sx={{
              ...surfaceSx,
              position: "relative",
              width: { xs: "100%", sm: "auto" },
              maxWidth: "none",
              mx: { xs: 0, sm: 2 },
            }}
          >
            {content}
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      data-cookie-banner-display={display}
      sx={{
        ...surfaceSx,
        position: "fixed",
        left: { xs: "16px", sm: "24px" },
        right: { xs: "16px", sm: "24px" },
        bottom: { xs: "16px", sm: "24px" },
        zIndex: (theme) => theme.zIndex.modal - 1,
        mx: "auto",
      }}
    >
      {content}
    </Box>
  );
}

const surfaceSx = {
  maxWidth: 900,
  p: { xs: 2, sm: 3 },
  borderRadius: 4,
  border: "1px solid rgba(255, 255, 255, 0.24)",
  backgroundColor: "rgba(82, 82, 82, 0.88)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  boxShadow: 10,
  color: "common.white",
  animation: "cookie-banner-enter 280ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
  "@keyframes cookie-banner-enter": {
    from: { opacity: 0, transform: "translateY(32px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

const actionButtonSx = {
  width: { xs: "100%", sm: 200 },
  minHeight: 58,
  py: 1,
  bgcolor: "common.white",
  color: "common.black",
  boxShadow: "none",
  fontSize: 16,
  "&:hover": {
    bgcolor: "grey.100",
    boxShadow: "none",
  },
};

const modalButtonSx = {
  width: { xs: "100%", sm: 200 },
  minHeight: 58,
  py: 1,
  boxShadow: "none",
  fontSize: 16,
};

const modalSecondaryButtonSx = {
  ...modalButtonSx,
  borderColor: "divider",
  color: "text.primary",
  "&:hover": {
    borderColor: "text.secondary",
    backgroundColor: "action.hover",
  },
};

const modalPrimaryButtonSx = {
  ...modalButtonSx,
  "&:hover": {
    boxShadow: "none",
  },
};
